import { isRecentUserInitiatedReaction } from 'Modules/ui/emoji/emojiReactions/utils/userReactionTracker';
import * as React from 'react';

interface UseAutoScrollReactionParams {
  enabled: boolean;
  newestMessageId: string | null | undefined;
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | undefined>;
  scrollIntoViewDelay?: number;
}

const DEFAULT_SCROLL_INTO_VIEW_DELAY_MS = 150;

/**
 * Watches for emoji reactions being added to the newest message.
 * If the newest message is visible and the user initiated the reaction,
 * scrolls the message into view when a reaction is added.
 */
export function useAutoScrollReaction({
  enabled,
  newestMessageId,
  scrollContainerRef,
  scrollIntoViewDelay = DEFAULT_SCROLL_INTO_VIEW_DELAY_MS,
}: UseAutoScrollReactionParams): void {
  React.useEffect(() => {
    if (!enabled || !newestMessageId || !scrollContainerRef.current) {
      return;
    }

    const messageId = newestMessageId;
    let observer: MutationObserver | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const observe = (el: HTMLElement) => {
      observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const n of m.addedNodes) {
            if (
              n.nodeType === Node.ELEMENT_NODE &&
              (n as Element)
                .getAttribute('data-testid')
                ?.includes('emoji-reaction')
            ) {
              if (!isRecentUserInitiatedReaction()) {
                return;
              }

              const container = scrollContainerRef.current;

              if (container) {
                const messageRect = el.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                const isMessageVisible =
                  messageRect.bottom > containerRect.top &&
                  messageRect.top < containerRect.bottom;

                if (isMessageVisible) {
                  setTimeout(
                    () =>
                      el.scrollIntoView({ behavior: 'smooth', block: 'end' }),
                    scrollIntoViewDelay
                  );
                }
              }

              return;
            }
          }
        }
      });

      observer.observe(el, { childList: true, subtree: true });
    };

    const el = document.getElementById(messageId);

    if (el) {
      observe(el);
    } else {
      timeoutId = setTimeout(() => {
        const retry = document.getElementById(messageId);
        if (retry) {
          observe(retry);
        }
      }, 100);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      observer?.disconnect();
    };
  }, [enabled, newestMessageId, scrollContainerRef, scrollIntoViewDelay]);
}
